import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const unique = [];
  const seen = new Set();
  payload.forEach((p) => {
    if (!seen.has(p.dataKey)) {
      seen.add(p.dataKey);
      unique.push(p);
    }
  });

  return (
    <div className="bg-gray-900 border border-gray-700/60 rounded-xl px-4 py-3 shadow-2xl shadow-black/40 min-w-[140px]">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
        {label}
      </p>
      <div className="flex flex-col gap-1.5">
        {unique.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: p.color }}
              />
              <span className="text-[12px] text-gray-300 truncate max-w-[90px]">
                {p.name}
              </span>
            </div>
            <span className="text-[12px] font-semibold tabular-nums text-gray-300">
              {p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Custom Legend ─────────────────────────────────────────────────────────────
const CustomLegend = ({ payload }) => {
  if (!payload?.length) return null;

  const unique = payload.filter(
    (item, index, self) =>
      index === self.findIndex((p) => p.value === item.value)
  );

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-3 pb-1 px-1">
      {unique.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block w-5 h-[2.5px] rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-[11px] font-medium text-gray-400">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Metric Card ───────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, color }) => (
  <div className="flex-1 min-w-0 bg-white border border-slate-300 rounded-xl px-3.5 py-3">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <p className="text-[11px] text-gray-500 truncate">{label}</p>
    </div>
    <p className="text-lg font-semibold text-slate-800 tabular-nums leading-none">
      {value.toLocaleString()}
    </p>
  </div>
);

// ─── Main Chart ────────────────────────────────────────────────────────────────
export default function ProductWarrantyChart({
  data,
  startDate,
  endDate,
  selectedMonth,
  selectedYear,
}) {
  const DASHBOARD_COLORS = [
    "#818cf8", // indigo
    "#34d399", // emerald
    "#fb923c", // orange
    "#38bdf8", // sky
    "#e879f9", // fuchsia
  ];

  // 🔥 format header date — original logic preserved
  const formatDisplayDate = () => {
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString("en-GB")} – ${endDate.toLocaleDateString("en-GB")}`;
    }
    return new Date(selectedYear, selectedMonth).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };

  // 🔹 get dynamic product keys — original logic preserved
  const productKeys = Array.from(
    new Set(
      data.flatMap((item) =>
        Object.keys(item).filter((key) => key !== "date")
      )
    )
  );

  // compute totals for metric cards
  const totals = productKeys.reduce((acc, key) => {
    acc[key] = data.reduce((sum, row) => sum + (row[key] ?? 0), 0);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-2xl p-5 shadow-xl">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="text-[15px] font-semibold text-slate-800 tracking-tight">
            Product Warranty Sales
          </h3>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Product-wise warranty sales trend
          </p>
        </div>

        {/* 🔥 date range badge — original logic preserved */}
        <div className="flex-shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-slate-200 border border-slate-300 text-slate-700 tracking-wide">
          {formatDisplayDate()}
        </div>
      </div>

      {/* ── Metric Cards ── */}
      {productKeys.length > 0 && (
        <div className="flex gap-2.5 mb-5 overflow-x-auto pb-0.5">
          {productKeys.map((key, i) => (
            <MetricCard
              key={key}
              label={key}
              value={totals[key]}
              color={DASHBOARD_COLORS[i % DASHBOARD_COLORS.length]}
            />
          ))}
        </div>
      )}

      {/* ── Chart ── */}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* Added deliberate padding to prevent canvas layout clipping */}
          <LineChart data={data} margin={{ top: 25, right: 15, bottom: 15, left: -15 }}>

            <defs>
              {productKeys.map((key, index) => {
                const color = DASHBOARD_COLORS[index % DASHBOARD_COLORS.length];
                return (
                  <g key={key}>
                    {/* Linear Gradient for Area Fill */}
                    <linearGradient id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                      <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>

                    {/* 🔥 Manual multi-stage glow filter with strict positioning rules */}
                    <filter 
                      id={`shadow-${key}`} 
                      x="-30%" 
                      y="-30%" 
                      width="160%" 
                      height="180%"
                      filterUnits="userSpaceOnUse"
                    >
                      {/* Step 1: Blur the source line data */}
                      <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
                      {/* Step 2: Move the blurred graphic downwards */}
                      <feOffset in="blur" dx="0" dy="8" result="offsetBlur" />
                      {/* Step 3: Colorize the blurred shadow using floodColor */}
                      <feFlood floodColor={color} floodOpacity="0.45" result="colorGlow" />
                      {/* Step 4: Map the color layer directly inside the blurred offset */}
                      <feComposite in="colorGlow" in2="offsetBlur" operator="in" result="shadow" />
                      {/* Step 5: Render original crisp line right back on top of the shadow */}
                      <feMerge>
                        <feMergeNode in="shadow" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </g>
                );
              })}
            </defs>

            <CartesianGrid
              stroke="rgba(0,0,0,0.04)"
              strokeDasharray="4 4"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              interval={0}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) =>
                String(new Date(value).getDate()).padStart(2, "0")
              }
            />

            <YAxis
              tick={{ fontSize: 10, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "rgba(0,0,0,0.06)",
                strokeWidth: 1,
                strokeDasharray: "4 4",
              }}
            />

            {/* Area fills */}
            {productKeys.map((key, index) => (
              <Area
                key={`area-${key}`}
                type="monotone"
                dataKey={key}
                stroke="none"
                fill={`url(#gradient-${key})`}
                legendType="none"
              />
            ))}

            {/* Lines with shadow filters */}
            {productKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
                strokeWidth={3}
                dot={false}
                // 🔥 Critical rendering bypass switches
                filter={`url(#shadow-${key})`}
                clipPath="none" 
                isAnimationActive={false}
                activeDot={{
                  r: 5,
                  fill: DASHBOARD_COLORS[index % DASHBOARD_COLORS.length],
                  stroke: "#fff",
                  strokeWidth: 2,
                }}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// import {
//   LineChart,
//   Line,
//   Area,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Legend,
// } from "recharts";

// // 🔹 Tooltip
// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload?.length) {
//     const unique = [];
//     const seen = new Set();

//     payload.forEach((p) => {
//       if (!seen.has(p.dataKey)) {
//         seen.add(p.dataKey);
//         unique.push(p);
//       }
//     });

//     return (
//       <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
//         <p className="font-semibold mb-1">{label}</p>

//         {unique.map((p, i) => (
//           <p key={i} style={{ color: p.color }}>
//             {p.name}: {p.value}
//           </p>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

// export default function ProductWarrantyChart({
//   data,
//   startDate,
//   endDate,
//   selectedMonth,
//   selectedYear,
// }) {

//   // 🔥 format header date
//   const formatDisplayDate = () => {

//     // if custom date range selected
//     if (startDate && endDate) {
//       return `${startDate.toLocaleDateString("en-GB")} - ${endDate.toLocaleDateString("en-GB")}`;
//     }

//     // fallback → selected month/year
//     return new Date(
//       selectedYear,
//       selectedMonth
//     ).toLocaleString("default", {
//       month: "long",
//       year: "numeric",
//     });
//   };

//   // 🔹 get dynamic product keys
//   const productKeys = Array.from(
//     new Set(
//       data.flatMap((item) =>
//         Object.keys(item).filter((key) => key !== "date")
//       )
//     )
//   );

//   const DASHBOARD_COLORS = [
//     "#22d3ee",
//     "#a78bfa",
//     "#34d399",
//     "#60a5fa",
//     "#f472b6",
//   ];

//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">

//       {/* Header */}
//       <div className="mb-3 flex items-center justify-between">

//         <div>
//           <h3 className="text-sm font-semibold text-slate-800">
//             Product Warranty Sales
//           </h3>

//           <p className="text-[11px] text-slate-400">
//             Product-wise warranty sales trend
//           </p>
//         </div>

//         {/* 🔥 DATE RANGE SHOW */}
//         <div className="text-[11px] px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
//           {formatDisplayDate()}
//         </div>

//       </div>

//       {/* Chart */}
//       <div style={{ height: 350 }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <LineChart data={data}>

//             <defs>
//               {productKeys.map((key, index) => (
//                 <linearGradient
//                   key={key}
//                   id={`gradient-${key}`}
//                   x1="0"
//                   y1="0"
//                   x2="0"
//                   y2="1"
//                 >
//                   <stop
//                     offset="0%"
//                     stopColor={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                     stopOpacity={0.25}
//                   />
//                   <stop
//                     offset="100%"
//                     stopColor={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                     stopOpacity={0}
//                   />
//                 </linearGradient>
//               ))}

//               <filter id="lineShadow" x="-50%" y="-50%" width="200%" height="200%">
//                 <feDropShadow
//                   dx="0"
//                   dy="2"
//                   stdDeviation="3"
//                   floodColor="#000000"
//                   floodOpacity="0.2"
//                 />
//               </filter>
//             </defs>

//             <CartesianGrid
//               stroke="#e5e7eb"
//               strokeDasharray="3 3"
//               vertical={false}
//             />

//             <XAxis
//               dataKey="date"
//               interval={0}
//               tick={{ fontSize: 10 }}
//               angle={0}
//               textAnchor="middle"
//               tickFormatter={(value) =>
//                 String(new Date(value).getDate()).padStart(2, "0")
//               }
//             />

//             <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />

//             <Tooltip content={<CustomTooltip />} />

//             <Legend />

//             {productKeys.map((key, index) => (
//               <Area
//                 key={`area-${key}`}
//                 type="monotone"
//                 dataKey={key}
//                 stroke="none"
//                 fill={`url(#gradient-${key})`}
//                 legendType="none"
//               />
//             ))}

//             {productKeys.map((key, index) => (
//               <Line
//                 key={key}
//                 type="monotone"
//                 dataKey={key}
//                 stroke={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                 strokeWidth={3}
//                 dot={false}
//                 activeDot={{ r: 5 }}
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 filter="url(#lineShadow)"
//               />
//             ))}
//           </LineChart>
//         </ResponsiveContainer>
//       </div>
//     </div>
//   );
// }

// import {
//   LineChart,
//   Line,
//   Area,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Legend,
// } from "recharts";

// // 🔹 Tooltip (slightly improved color sync)
// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload?.length) {

//     // ✅ remove duplicate entries (Area + Line)
//     const unique = [];
//     const seen = new Set();

//     payload.forEach((p) => {
//       if (!seen.has(p.dataKey)) {
//         seen.add(p.dataKey);
//         unique.push(p);
//       }
//     });

//     return (
//       <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
//         <p className="font-semibold mb-1">{label}</p>

//         {unique.map((p, i) => (
//           <p key={i} style={{ color: p.color }}>
//             {p.name}: {p.value}
//           </p>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

// export default function ProductWarrantyChart({ data }) {
//   // 🔹 get dynamic product keys
//   const productKeys = Array.from(
//     new Set(
//       data.flatMap((item) =>
//         Object.keys(item).filter((key) => key !== "date")
//       )
//     )
//   );

//   // 🔹 dashboard color system (same as donut)
//   const DASHBOARD_COLORS = [
//     "#22d3ee", // cyan
//     "#a78bfa", // purple
//     "#34d399", // green
//     "#60a5fa", // blue
//     "#f472b6", // pink
//   ];

//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       {/* Header */}
//       <div className="mb-3">
//         <h3 className="text-sm font-semibold text-slate-800">
//           Product Warranty Sales
//         </h3>
//         <p className="text-[11px] text-slate-400">
//           Product-wise warranty sales trend
//         </p>
//       </div>

//       {/* Chart */}
//       <div style={{ height: 350 }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <LineChart data={data}>
            
//             {/* 🔥 Gradient defs */}
//             <defs>
//               {productKeys.map((key, index) => (
//                 <linearGradient
//                   key={key}
//                   id={`gradient-${key}`}
//                   x1="0"
//                   y1="0"
//                   x2="0"
//                   y2="1"
//                 >
//                   <stop
//                     offset="0%"
//                     stopColor={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                     stopOpacity={0.25}
//                   />
//                   <stop
//                     offset="100%"
//                     stopColor={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                     stopOpacity={0}
//                   />
//                 </linearGradient>
//               ))}

//               {/* 🔥 ADD THIS SHADOW FILTER */}
//               <filter id="lineShadow" x="-50%" y="-50%" width="200%" height="200%">
//                 <feDropShadow
//                   dx="0"
//                   dy="2"
//                   stdDeviation="3"
//                   floodColor="#000000"
//                   floodOpacity="0.2"
//                 />
//               </filter>
//             </defs>

//             {/* Grid */}
//             <CartesianGrid
//               stroke="#e5e7eb"
//               strokeDasharray="3 3"
//               vertical={false}
//             />

//             {/* Axes */}
//             <XAxis
//               dataKey="date"
//               interval={0}
//               tick={{ fontSize: 10 }}
//               angle={0}
//               textAnchor="middle"
//               tickFormatter={(value) =>
//                 String(new Date(value).getDate()).padStart(2, "0")
//               }
//             />
//             <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />

//             {/* Tooltip + Legend */}
//             <Tooltip content={<CustomTooltip />} />
//             <Legend />

//             {/* 🔥 Area (gradient fill) */}
//             {productKeys.map((key, index) => (
//               <Area
//                   key={`area-${key}`}
//                   type="monotone"
//                   dataKey={key}
//                   stroke="none"
//                   fill={`url(#gradient-${key})`}
//                   legendType="none"   // 🔥 ADD THIS
//                 />
//             ))}

//             {/* 🔥 Lines (smooth modern style) */}
//             {productKeys.map((key, index) => (
//               <Line
//                 key={key}
//                 type="monotone"
//                 dataKey={key}
//                 stroke={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                 strokeWidth={3}
//                 dot={false}
//                 activeDot={{ r: 5 }}
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 filter="url(#lineShadow)"   // 🔥 ADD THIS
//               />
//             ))}
//           </LineChart>
//         </ResponsiveContainer>
//       </div>
//     </div>
//   );
// }


// import {
//   LineChart,
//   Line,
//   XAxis,
//   YAxis,
//   Tooltip,
//   ResponsiveContainer,
//   CartesianGrid,
//   Legend,
// } from "recharts";

// const CustomTooltip = ({ active, payload, label }) => {
//   if (active && payload?.length) {
//     return (
//       <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
//         <p className="font-semibold mb-1">{label}</p>
//         {payload.map((p, i) => (
//           <p key={i} className="text-blue-300">
//             {p.name}: {p.value}
//           </p>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

// export default function ProductWarrantyChart({ data }) {

//     const productKeys = Array.from(
//         new Set(
//             data.flatMap((item) =>
//             Object.keys(item).filter((key) => key !== "date")
//             )
//         )
//         );

//     const DASHBOARD_COLORS = [
//       "#22d3ee", // cyan (onboarding)
//       "#a78bfa", // purple (customers)
//       "#34d399", // green (sales)
//       "#60a5fa", // light blue (extra)
//       "#f472b6", // pink (extra)
//     ];
        
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <div className="mb-3">
//         <h3 className="text-sm font-semibold text-slate-800">
//           Product Warranty Sales
//         </h3>
//         <p className="text-[11px] text-slate-400">
//           Product-wise warranty sales trend
//         </p>
//       </div>

//       <div style={{ height: 250 }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <LineChart data={data}>
//             <CartesianGrid stroke="#f1f5f9" vertical={false} />

//             <XAxis
//               dataKey="date"
//               tick={{ fontSize: 10 }}
//             />

//             <YAxis
//               tick={{ fontSize: 10 }}
//               allowDecimals={false}
//             />

//             <Tooltip content={<CustomTooltip />} />
//             <Legend />

//             {/* 🔥 Multiple product lines */}
//            {productKeys.map((key, index) => (
//                 <Line
//                     key={key}
//                     type="monotone"
//                     dataKey={key}
//                     stroke={DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]}
//                     strokeWidth={2}
//                     dot={false}
//                 />
//                 ))}
//           </LineChart>
//         </ResponsiveContainer>
//       </div>
//     </div>
//   );
// }